import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

import type { NavItem } from 'types/client/navigation';

import useNavItems, { isGroupItem, isInternalItem } from 'lib/hooks/useNavItems';
import { useColorModeValue } from 'toolkit/chakra/color-mode';
import Settings from 'ui/snippets/topBar/settings/Settings';

import type { BasisLink } from './basisLinks';
import { PRODUCT_LINKS, PROJECT_LINKS, SCHEDULE_URL, SCHEDULE_LABEL, DISCORD_URL } from './basisLinks';
import BasisLogo from './BasisLogo';
import DiscordMark from './DiscordMark';

// [Basis chrome] La navbar del explorador es la de la landing.
//
// Port de frontend_landing/src/components/AppHeader.astro. Misma estructura,
// mismos rotulos, mismos desplegables por :hover/:focus-within sin JavaScript;
// los estilos viven en nextjs/basis-chrome.css con los valores de globals.css.
//
// TRES DESVIACIONES, todas deliberadas:
//
//  1. Un tercer desplegable, «Explorador», que la landing no tiene. Sin el, el
//     explorador se queda literalmente sin navegacion en escritorio: Bloques,
//     Transacciones y Cuentas solo son alcanzables por URL. Se construye desde
//     el MISMO useNavItems() que alimenta la navegacion original, asi que
//     hereda lo que cada env enciende o apaga y no hay una segunda lista que
//     mantener. Visualmente es identico a «Producto» y «Proyecto».
//
//  2. Donde la landing pone el selector de idioma va el engranaje de ajustes de
//     Blockscout (tema claro/oscuro, formato de direccion, identicon). El
//     explorador no es multilingue -- los rotulos de useNavItems estan en ingles
//     en el upstream -- y el conmutador de tema si tiene que estar en algun
//     sitio: es la unica via para el modo oscuro.
//
//  3. El buscador no se mete aqui. Blockscout ya lo pinta con HeaderDesktop en
//     lo alto del area de contenido cuando la navegacion es horizontal, que es
//     el modo que este chrome activa. Duplicarlo seria dos cajas de busqueda.
//
// El resto -- marca, «Producto», «Proyecto», Discord, el CTA cian, la
// hamburguesa y el panel movil -- es la landing, elemento por elemento.
//
// FONDO DE LA BARRA (landing 4d4d122). Transparente arriba del todo y vidrio
// blanco al bajar: el <nav> lleva `data-solid` cuando el scroll pasa de 4 px o
// cuando el menu movil esta abierto --si no, flotaria transparente sobre la capa
// que tapa la pagina--. Los estilos de los dos estados estan en basis-chrome.css.
//
// QUIEN SE DESPLAZA. En la landing es <body> (html y body miden 100% con el
// overflow-x oculto, y quien acaba desplazandose es body), asi que alli el
// script escucha `document` en captura y lee el scrollTop de los dos. En
// Blockscout NO: ninguno de los Layout envuelve el contenido en un contenedor
// con overflow (LayoutApp si, pero es la pagina de
// dapps, que este despliegue no enciende y que ni siquiera monta esta navbar),
// asi que se desplaza la VENTANA. Se escucha `window` y se lee `scrollY`.
// Como en la landing: un fotograma como maximo por evento (rAF), y se vuelve a
// medir al montar y en `pageshow`, porque quien llega con un ancla o vuelve con
// el boton atras aterriza ya desplazado, a veces sin un solo evento de scroll.

// Desplazamiento a partir del cual la barra pasa de transparente a vidrio: el
// mismo umbral de la landing (scrollY > 4).
const SOLID_AFTER_PX = 4;

const Chevron = () => (
  <svg className="bs-menu__chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
  </svg>
);

// Aplana los subItems de un grupo: useNavItems los agrupa en sublistas para
// dibujar separadores en la navegacion original, que aqui no se usan.
function flattenSubItems(subItems: Array<NavItem> | Array<Array<NavItem>>): Array<NavItem> {
  return subItems.flat() as Array<NavItem>;
}

interface MenuProps {
  label: string;
  ariaLabel: string;
  children: React.ReactNode;
  narrow?: boolean;
}

const Menu = ({ label, ariaLabel, children, narrow }: MenuProps) => (
  <div className="bs-menu">
    <button type="button" aria-haspopup="menu" aria-label={ ariaLabel } className="bs-menu__button">
      { label }
      <Chevron/>
    </button>
    <div aria-hidden="true" className="bs-menu__bridge"/>
    <div role="menu" className={ `bs-menu__panel${ narrow ? ' bs-menu__panel--narrow' : '' }` }>
      { children }
    </div>
  </div>
);

const ExternalItem = ({ text, url }: BasisLink) => (
  <a href={ url } role="menuitem" className="bs-menu__item">{ text }</a>
);

// Un elemento del explorador. Interno va por next/link para conservar la
// navegacion de cliente; externo (el enlace a la documentacion de la API que
// useNavItems puede devolver como url) va por <a>.
const ExplorerItem = ({ item, onClick }: { item: NavItem; onClick?: () => void }) => {
  if (isInternalItem(item)) {
    return (
      <NextLink
        href={ item.nextRoute }
        role="menuitem"
        className="bs-menu__item"
        aria-current={ item.isActive ? 'page' : undefined }
        onClick={ onClick }
      >
        { item.text }
      </NextLink>
    );
  }

  return <a href={ item.url } role="menuitem" className="bs-menu__item" onClick={ onClick }>{ item.text }</a>;
};

const BasisNavBar = () => {
  const { mainNavItems } = useNavItems();
  const router = useRouter();
  const [ isMenuOpen, setMenuOpen ] = React.useState(false);

  // El wordmark en Gris oscuro sobre la barra clara y en Blanco sobre la oscura:
  // la pagina del tema oscuro es #3c3c3c y el wordmark oscuro desapareceria.
  const logoVariant = useColorModeValue<'ink' | 'white'>('ink', 'white');

  const [ isScrolled, setScrolled ] = React.useState(false);

  // Los elementos del explorador, ya aplanados: los sueltos tal cual y los
  // grupos («Blockchain», «Tokens», «Other») expandidos a sus hijos, que es lo
  // que un desplegable de un solo nivel puede mostrar.
  const explorerItems = React.useMemo(() => {
    return mainNavItems.flatMap(item => isGroupItem(item) ? flattenSubItems(item.subItems) : [ item ]);
  }, [ mainNavItems ]);

  const closeMenu = React.useCallback(() => setMenuOpen(false), []);
  const toggleMenu = React.useCallback(() => setMenuOpen(open => !open), []);

  // Fondo de la barra: ver la nota de la cabecera. El estado solo cambia cuando
  // cruza el umbral, asi que el rAF no provoca un render por cada evento.
  React.useEffect(() => {
    let frame = 0;
    let queued = false;

    const paintBar = () => setScrolled(window.scrollY > SOLID_AFTER_PX);

    const onScroll = () => {
      if (queued) {
        return;
      }
      queued = true;
      frame = window.requestAnimationFrame(() => {
        queued = false;
        paintBar();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pageshow', paintBar);
    paintBar();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pageshow', paintBar);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  // El panel movil se cierra al navegar. Sin esto queda abierto sobre la pagina
  // nueva, porque una navegacion de cliente no desmonta la navbar.
  React.useEffect(() => {
    router.events.on('routeChangeComplete', closeMenu);
    return () => router.events.off('routeChangeComplete', closeMenu);
  }, [ router.events, closeMenu ]);

  return (
    <header className="bs-chrome bs-navbar">
      <nav
        className="bs-navbar__bar"
        data-header-bar
        data-solid={ isScrolled || isMenuOpen ? '' : undefined }
      >
        { /* relative z-50 (bs-navbar__inner): por encima de .bs-navlayer, la capa
             que cubre la pagina con el menu movil abierto. Sin esto, el primer
             toque en la hamburguesa o en el engranaje caia en la capa y solo
             cerraba el menu. */ }
        <div className="bs-navbar__inner">
          <BasisLogo variant={ logoVariant }/>

          { /* Escritorio */ }
          <div className="bs-navbar__center">
            <Menu label="Explorador" ariaLabel="Abrir menú Explorador">
              <NextLink href={{ pathname: '/' }} role="menuitem" className="bs-menu__item">Inicio</NextLink>
              { explorerItems.map(item => <ExplorerItem key={ item.text } item={ item }/>) }
            </Menu>
            <Menu label="Producto" ariaLabel="Abrir menú Producto">
              { PRODUCT_LINKS.map(link => <ExternalItem key={ link.text } { ...link }/>) }
            </Menu>
            <Menu label="Proyecto" ariaLabel="Abrir menú Proyecto" narrow>
              { PROJECT_LINKS.map(link => <ExternalItem key={ link.text } { ...link }/>) }
            </Menu>
          </div>

          { /* Cluster derecho */ }
          <div className="bs-navbar__right">
            <div className="bs-iconbtn-slot">
              <Settings/>
            </div>

            <a
              href={ DISCORD_URL }
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Únete a la comunidad de Basis Network en Discord"
              title="Discord"
              className="bs-iconbtn"
            >
              <DiscordMark size={ 17 }/>
            </a>

            <a href={ SCHEDULE_URL } className="bs-btn bs-btn--cyan bs-navbar__cta">
              { SCHEDULE_LABEL }
            </a>

            <button
              type="button"
              aria-label="Menú"
              aria-expanded={ isMenuOpen }
              className="bs-burger"
              onClick={ toggleMenu }
            >
              <div aria-hidden="true" className="bs-burger__line"/>
              <div aria-hidden="true" className="bs-burger__line"/>
            </button>
          </div>
        </div>

        { /* Movil */ }
        <div
          aria-hidden="true"
          className={ `bs-navlayer${ isMenuOpen ? ' bs-navlayer--open' : '' }` }
          onClick={ closeMenu }
        />
        { isMenuOpen && (
          <div className="bs-mobilemenu">
            <div className="bs-mobilemenu__body">
              <div className="bs-mobilemenu__group">
                <p className="bs-mobilemenu__label">Explorador</p>
                <NextLink href={{ pathname: '/' }} className="bs-mobilemenu__item" onClick={ closeMenu }>Inicio</NextLink>
                { explorerItems.map(item => (
                  isInternalItem(item) ? (
                    <NextLink
                      key={ item.text }
                      href={ item.nextRoute }
                      className="bs-mobilemenu__item"
                      aria-current={ item.isActive ? 'page' : undefined }
                      onClick={ closeMenu }
                    >
                      { item.text }
                    </NextLink>
                  ) : (
                    <a key={ item.text } href={ item.url } className="bs-mobilemenu__item" onClick={ closeMenu }>{ item.text }</a>
                  )
                )) }
              </div>

              <div className="bs-mobilemenu__group">
                <p className="bs-mobilemenu__label">Producto</p>
                { PRODUCT_LINKS.map(link => (
                  <a key={ link.text } href={ link.url } className="bs-mobilemenu__item">{ link.text }</a>
                )) }
              </div>

              <div className="bs-mobilemenu__group">
                <p className="bs-mobilemenu__label">Proyecto</p>
                { PROJECT_LINKS.map(link => (
                  <a key={ link.text } href={ link.url } className="bs-mobilemenu__item">{ link.text }</a>
                )) }
              </div>

              <div className="bs-mobilemenu__actions">
                <a href={ SCHEDULE_URL } className="bs-btn bs-btn--cyan">{ SCHEDULE_LABEL }</a>
                <a href={ DISCORD_URL } target="_blank" rel="noopener noreferrer" className="bs-btn bs-btn--ghost">
                  <DiscordMark size={ 17 } style={{ marginRight: '0.625rem' }}/>
                  Únete al Discord
                </a>
              </div>
            </div>
          </div>
        ) }
      </nav>
    </header>
  );
};

export default React.memo(BasisNavBar);
